import React, { useEffect, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import { Plug, CheckCircle, XCircle, ArrowsClockwise, ArrowSquareOut, Warning, CircleNotch } from "@phosphor-icons/react";

const fmtDateTime = (iso) => {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function StatusBadge({ status }) {
  if (!status?.configured) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#012340]/8 text-[#5C738A]" data-testid="zoho-status-badge"><Warning size={12} weight="bold" /> Not Configured</span>;
  }
  if (!status?.connected) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#F57C00]/15 text-[#F57C00]" data-testid="zoho-status-badge"><XCircle size={12} weight="bold" /> Disconnected</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#2E7D32]/12 text-[#2E7D32]" data-testid="zoho-status-badge"><CheckCircle size={12} weight="bold" /> Connected</span>;
}

export default function IntegrationsSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/zoho/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // Honour ?zoho=connected / ?zoho=error redirects
    const params = new URLSearchParams(window.location.search);
    if (params.get("zoho") === "connected") {
      toast.success("Zoho Books connected");
      window.history.replaceState({}, "", "/admin/integrations");
    }
    if (params.get("zoho") === "error") {
      const detail = params.get("detail") || "Connection failed";
      toast.error(`Zoho connect failed: ${detail}`, { duration: 12000 });
      window.history.replaceState({}, "", "/admin/integrations");
    }
    // When the user returns to this tab after completing OAuth in a new tab,
    // refresh status so the badge flips to "Connected" without a manual reload.
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  async function connect() {
    setBusy(true);
    try {
      // Pass the browser's actual page origin so the backend builds a redirect_uri
      // that matches the user's current domain — Cloudflare/ingress can rewrite
      // Origin/Referer headers, so we trust window.location.origin instead.
      const { data } = await api.get("/integrations/zoho/oauth/start", {
        params: { frontend_origin: window.location.origin },
      });
      if (data?.authorize_url) {
        try {
          if (window.top && window.top !== window) {
            window.top.location.href = data.authorize_url;
            return;
          }
        } catch (_e) { /* cross-origin top — fall through */ }
        const w = window.open(data.authorize_url, "_blank", "noopener,noreferrer");
        if (!w) {
          window.location.href = data.authorize_url;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start OAuth flow");
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Zoho Books? This wipes the local mirror but keeps all customer links.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/zoho/disconnect");
      toast.success("Zoho disconnected");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  }

  async function syncNow() {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/zoho/sync");
      const s = data?.stats || {};
      toast.success(`Sync complete · ${s.contacts || 0} contacts, ${s.invoices || 0} invoices, ${s.payments || 0} payments`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="integrations-settings">
      <div className="mb-5 flex-shrink-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5C738A]">Settings</div>
        <h1 className="font-heading text-3xl font-black text-[#012340] mt-1">Integrations</h1>
        <p className="text-sm text-[#5C738A] mt-1">Connect third-party services. Billing data flows in read-only — ZMEX Hub never edits invoices or charges customers.</p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {/* Zoho Books card */}
        <div className="bg-white border border-[#012340]/10 rounded-sm p-6 max-w-3xl" data-testid="zoho-integration-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-sm bg-[#012340] text-[#CFAB7A] grid place-items-center flex-shrink-0">
              <Plug size={26} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-heading text-xl font-bold text-[#012340]">Zoho Books</h2>
                {loading ? <CircleNotch size={14} className="animate-spin text-[#5C738A]" /> : <StatusBadge status={status} />}
              </div>
              <p className="text-sm text-[#5C738A] mt-1">
                Read-only sync of contacts, invoices, and customer payments. Used to surface A/R, balances, and aging inside ZMEX Hub.
              </p>

              {!loading && !status?.configured && (
                <div className="mt-4 bg-[#FAFAFA] border border-dashed border-[#012340]/15 rounded-sm p-4 text-sm text-[#5C738A]" data-testid="zoho-not-configured">
                  <div className="font-bold text-[#012340] mb-1">Setup required</div>
                  Add the following to <span className="font-mono text-[#012340]">backend/.env</span> and restart the backend, then come back here to connect:
                  <ul className="font-mono text-xs mt-2 space-y-0.5">
                    <li>ZOHO_CLIENT_ID=…</li>
                    <li>ZOHO_CLIENT_SECRET=…</li>
                    <li>ZOHO_REDIRECT_URI=…/api/integrations/zoho/oauth/callback</li>
                    <li>ZOHO_ORGANIZATION_ID=…</li>
                  </ul>
                  <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-[#012340] font-bold hover:text-[#CFAB7A]">
                    Open Zoho API Console <ArrowSquareOut size={12} weight="bold" />
                  </a>
                </div>
              )}

              {!loading && status?.configured && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="zoho-status-grid">
                  <Info label="Organization" value={status.org_name || status.org_id || "—"} />
                  <Info label="Last Sync" value={fmtDateTime(status.last_sync_at)} />
                  <Info label="Last Sync Status" value={status.last_sync_status || "—"} />
                  <Info label="Connected At" value={fmtDateTime(status.connected_at)} />
                </div>
              )}

              <div className="mt-5 flex items-center gap-2 flex-wrap">
                {!loading && status?.configured && !status?.connected && (
                  <button onClick={connect} disabled={busy} className="px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] hover:brightness-105 disabled:opacity-50" data-testid="zoho-connect-btn">
                    {busy ? "Redirecting…" : "Connect Zoho Books"}
                  </button>
                )}
                {!loading && status?.connected && (
                  <>
                    <button onClick={syncNow} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#012340]/90 disabled:opacity-50" data-testid="zoho-sync-btn">
                      <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} /> {busy ? "Syncing…" : "Sync Now"}
                    </button>
                    <button onClick={disconnect} disabled={busy} className="px-4 py-2 rounded-sm border border-[#E53935]/30 text-[#E53935] text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E53935]/5 disabled:opacity-50" data-testid="zoho-disconnect-btn">
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 text-xs text-[#5C738A] max-w-3xl">
          ZMEX Hub never writes back to Zoho — no invoices created, no payments recorded, no customers edited. Linking is always manual via the customer’s Billing tab.
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}</div>
      <div className="text-sm text-[#012340] font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}
